import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  ChevronDown, ChevronRight, Search, Link2, ShipWheel, Rocket,
  ShieldCheck, BookOpen, PlusCircle, Send, Loader2, CheckCircle2, Puzzle, Trash2, Pencil, GitFork, Clock, RefreshCw,
} from 'lucide-react';
import type { SignalTemplate, ActionTemplate, WorkflowIntegrationStatus, CustomBlock } from '../../api/client';
import { apiClient } from '../../api/client';
import { IS_DEMO_MODE } from '@/lib/demoMode';
import { useTheme } from '@/components/theme-provider';
import slackColorIcon from '../../assets/icons/slack-color.png';
import pagerdutyColorIcon from '../../assets/icons/pagerduty-icon.svg';
import argocdIcon from '../../assets/icons/argocd.svg';
import fluxcdIcon from '../../assets/icons/flux-cd.svg';
import karpenterIcon from '../../assets/icons/karpenter.svg';
import kyvernoIcon from '../../assets/icons/kyverno.svg';
import trivyIcon from '../../assets/icons/trivy.svg';
import helmLightIcon from '../../assets/icons/helm-light.svg';
import helmDarkIcon from '../../assets/icons/helm-dark.svg';
import confluenceIcon from '../../assets/icons/confluence.svg';
import jiraIcon from '../../assets/icons/jira.svg';
import datadogLightIcon from '../../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../../assets/icons/datadog-dark-mode.png';
import linearIcon from '../../assets/icons/linear.png';
import linearDarkIcon from '../../assets/icons/linear-dark-mode.png';
import gitlabIcon from '../../assets/icons/gitlab-clean.svg';
import kestrelLightLogo from '../../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../../assets/icons/kestrel-dark.svg';
import awsLightLogo from '../../assets/icons/aws.svg';
import awsDarkLogo from '../../assets/icons/aws-dark.svg';
import { ReactComponent as GCPIcon } from '../../assets/icons/gcp.svg';
import { sidebarGroupFor, orderSidebarActions } from './sidebarGrouping';
import posthogIcon from '../../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../../assets/icons/posthog-light.png';
import vercelIcon from '../../assets/icons/vercel.svg';
import vercelLightIcon from '../../assets/icons/vercel-light.svg';
import railwayIcon from '../../assets/icons/railway.svg';
import flyioIcon from '../../assets/icons/flyio.svg';
import nebiusIcon from '../../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../../assets/icons/daytona.svg';
import supabaseIcon from '../../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../../assets/icons/planetscale-mono.svg';
import neonIcon from '../../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../../assets/icons/clickhouse.svg';
import jenkinsIcon from '../../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../../assets/icons/okta-mono.svg';
import databricksIcon from '../../assets/icons/databricks.png';
import terraformIcon from '../../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../../assets/icons/infisical.svg';
import cloudflareIcon from '../../assets/icons/cloudflare.svg';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface WorkflowSidebarProps {
  signals: SignalTemplate[];
  actions: ActionTemplate[];
  integrations: WorkflowIntegrationStatus[];
  customBlocks?: CustomBlock[];
  onCreateCustomBlock?: () => void;
  onDeleteCustomBlock?: (blockId: string) => void;
  onEditCustomBlock?: (block: CustomBlock) => void;
  /** Mobile tap-to-add: called with the same payload drag-and-drop carries. */
  onBlockAdd?: (nodeType: string, data: any) => void;
}

interface GroupedActions {
  [integration: string]: ActionTemplate[];
}

function useIsDark() {
  const { theme } = useTheme();
  return theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
}

function getIntegrationIcon(id: string, isDark: boolean): React.ReactNode {
  switch (id) {
    case 'kestrel':
      return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" className="w-4 h-4" />;
    case 'github':
      return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" className="w-4 h-4" />;
    case 'gitlab':
      return <img src={gitlabIcon} alt="GitLab" className="w-4 h-4" />;
    case 'slack':
      return <img src={slackColorIcon} alt="Slack" className="w-4 h-4" />;
    case 'pagerduty':
      return <img src={pagerdutyColorIcon} alt="PagerDuty" className="w-4 h-4" />;
    case 'argocd':
      return <img src={argocdIcon} alt="ArgoCD" className="w-4 h-4" />;
    case 'argo-rollouts':
      return <img src={argocdIcon} alt="Argo Rollouts" className="w-4 h-4" />;
    case 'fluxcd':
      return <img src={fluxcdIcon} alt="Flux CD" className="w-4 h-4" />;
    case 'karpenter':
      return <img src={karpenterIcon} alt="Karpenter" className="w-4 h-4" />;
    case 'kyverno':
      return <img src={kyvernoIcon} alt="Kyverno" className="w-4 h-4" />;
    case 'trivy':
      return <img src={trivyIcon} alt="Trivy" className="w-4 h-4" />;
    case 'kubernetes':
      return <ShipWheel className="w-4 h-4 text-blue-500" />;
    case 'aws':
    case 'aws-cost':
    case 'aws-ops':
    case 'aws-security':
    case 'aws-data':
      return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className="w-4 h-4" />;
    case 'gcp-cost':
    case 'gcp':
    case 'gcp-security':
    case 'gcp-data':
      return <GCPIcon className="w-4 h-4" />;
    case 'confluence':
      return <img src={confluenceIcon} alt="Confluence" className="w-4 h-4" />;
    case 'jira':
      return <img src={jiraIcon} alt="Jira" className="w-4 h-4" />;
    case 'datadog':
      return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" className="w-4 h-4" />;
    case 'posthog':
      return <img src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" className="w-4 h-4 object-contain" />;
    case 'vercel':
      return <img src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" className="w-4 h-4" />;
    case 'railway':
      return <img src={railwayIcon} alt="Railway" className="w-4 h-4 object-contain" style={{ filter: isDark ? 'invert(1)' : 'none' }} />;
    case 'flyio':
      return <img src={flyioIcon} alt="Fly.io" className="w-4 h-4 object-contain" />;
    case 'nebius':
      return <img src={nebiusIcon} alt="Nebius" className="w-4 h-4 object-contain" />;
    case 'beam':
      return <BeamIcon className="w-4 h-4 text-foreground" />;
    case 'daytona':
      return <DaytonaIcon className="w-4 h-4 text-foreground" />;
    case 'supabase':
      return <img src={supabaseIcon} alt="Supabase" className="w-4 h-4 object-contain" />;
    case 'planetscale':
      return <PlanetScaleIcon className="w-4 h-4 text-foreground" />;
    case 'neon':
      return <img src={neonIcon} alt="Neon" className="w-4 h-4 object-contain" />;
    case 'clickhouse':
      return <img src={clickhouseIcon} alt="ClickHouse" className="w-4 h-4 object-contain" />;
    case 'jenkins':
      return <img src={jenkinsIcon} alt="Jenkins" className="w-4 h-4 object-contain" />;
    case 'circleci':
      return <CircleCIIcon className="w-4 h-4 text-foreground" />;
    case 'sonarcloud':
      return <SonarCloudIcon className="w-4 h-4 text-foreground" />;
    case 'okta':
      return <OktaIcon className="w-4 h-4 text-foreground" />;
    case 'databricks':
      return <img src={databricksIcon} alt="Databricks" className="w-4 h-4 object-contain" />;
    case 'terraform':
      return <img src={terraformIcon} alt="Terraform" className="w-4 h-4 object-contain" />;
    case 'pulumi':
      return <img src={pulumiIcon} alt="Pulumi" className="w-4 h-4 object-contain" />;
    case 'vault':
      return <img src={vaultIcon} alt="Vault" className="w-4 h-4 object-contain" />;
    case 'infisical':
      return <img src={infisicalIcon} alt="Infisical" className="w-4 h-4 object-contain rounded-sm" />;
    case 'linear':
      return <img src={isDark ? linearDarkIcon : linearIcon} alt="Linear" className="w-4 h-4" />;
    case 'cloudflare':
      return <img src={cloudflareIcon} alt="Cloudflare" className="w-4 h-4" />;
    case 'notion':
      return <BookOpen className="w-4 h-4" />;
    case 'approval':
      return <ShieldCheck className="w-4 h-4" />;
    case 'helm':
      return <img src={isDark ? helmDarkIcon : helmLightIcon} alt="Helm" className="w-4 h-4" />;
    default:
      return <Link2 className="w-4 h-4 text-muted-foreground" />;
  }
}

export function WorkflowSidebar({ signals, actions, integrations, customBlocks, onCreateCustomBlock, onDeleteCustomBlock, onEditCustomBlock, onBlockAdd }: WorkflowSidebarProps) {
  const isDark = useIsDark();
  const [search, setSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteBlock = async () => {
    if (!deleteConfirm || !onDeleteCustomBlock) return;
    setDeleting(true);
    try {
      // Demo blocks are client-side only; the parent removes them from state.
      if (!IS_DEMO_MODE) {
        await apiClient.deleteCustomBlock(deleteConfirm.id);
      }
      onDeleteCustomBlock(deleteConfirm.id);
    } catch {
      // silent — audit log captures failure
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const connectedSet = new Set(integrations.filter(i => i.connected).map(i => i.id));

  const grouped: GroupedActions = {};
  for (const a of actions) {
    const groupKey = sidebarGroupFor(a.integration);
    if (!grouped[groupKey]) grouped[groupKey] = [];
    grouped[groupKey].push(a);
  }
  for (const key of Object.keys(grouped)) {
    grouped[key] = orderSidebarActions(key, grouped[key]);
  }

  const lowerSearch = search.toLowerCase();
  const filteredK8sSignals = signals.filter(
    s => s.source === 'kubernetes' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredAWSSignals = signals.filter(
    s => s.source === 'aws' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredGCPSignals = signals.filter(
    s => s.source === 'gcp' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredRequestSignals = signals.filter(
    s => s.source === 'request' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredPDSignals = signals.filter(
    s => s.source === 'pagerduty' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredPostHogSignals = signals.filter(
    s => s.source === 'posthog' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredVercelSignals = signals.filter(
    s => s.source === 'vercel' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredRailwaySignals = signals.filter(
    s => s.source === 'railway' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredFlyioSignals = signals.filter(
    s => s.source === 'flyio' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredNebiusSignals = signals.filter(
    s => s.source === 'nebius' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredBeamSignals = signals.filter(
    s => s.source === 'beam' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredDaytonaSignals = signals.filter(
    s => s.source === 'daytona' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredSupabaseSignals = signals.filter(
    s => s.source === 'supabase' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredPlanetScaleSignals = signals.filter(
    s => s.source === 'planetscale' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredNeonSignals = signals.filter(
    s => s.source === 'neon' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredClickHouseSignals = signals.filter(
    s => s.source === 'clickhouse' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredJenkinsSignals = signals.filter(
    s => s.source === 'jenkins' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredCircleCISignals = signals.filter(
    s => s.source === 'circleci' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredSonarCloudSignals = signals.filter(
    s => s.source === 'sonarcloud' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredOktaSignals = signals.filter(
    s => s.source === 'okta' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredDatabricksSignals = signals.filter(
    s => s.source === 'databricks' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredKyvernoSignals = signals.filter(
    s => s.source === 'kyverno' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredTrivySignals = signals.filter(
    s => s.source === 'trivy' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredTerraformSignals = signals.filter(
    s => s.source === 'terraform' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredPulumiSignals = signals.filter(
    s => s.source === 'pulumi' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredGitHubSignals = signals.filter(
    s => s.source === 'github' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );
  const filteredScheduleSignals = signals.filter(
    s => s.source === 'schedule' && (!search || s.name.toLowerCase().includes(lowerSearch) || s.description.toLowerCase().includes(lowerSearch)),
  );

  const onDragStart = (event: React.DragEvent, nodeType: string, data: any) => {
    event.dataTransfer.setData('application/reactflow-type', nodeType);
    event.dataTransfer.setData('application/reactflow-data', JSON.stringify(data));
    event.dataTransfer.effectAllowed = 'move';
  };

  // Mobile tap-to-add: drag-and-drop is impractical on touch, so a tap on a
  // draggable block adds it to the canvas. Rather than wiring a click handler
  // into all ~30 block renderers, delegate from the root: replay a synthetic
  // dragstart on the tapped block with a stub DataTransfer and harvest the
  // exact payload the real drag path would carry (including each item's
  // connected/disabled guards, which preventDefault before setting data).
  const handleMobileTap = (e: React.MouseEvent) => {
    if (!onBlockAdd) return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    const target = e.target as HTMLElement;
    // Ignore taps on controls inside blocks (e.g. custom block edit/delete).
    if (target.closest('button')) return;
    const block = target.closest('[draggable="true"]');
    if (!block) return;

    const store: Record<string, string> = {};
    const fake = new Event('dragstart', { bubbles: true, cancelable: true });
    Object.defineProperty(fake, 'dataTransfer', {
      value: {
        setData: (key: string, value: string) => { store[key] = value; },
        effectAllowed: '',
      },
    });
    block.dispatchEvent(fake);

    const nodeType = store['application/reactflow-type'];
    const dataStr = store['application/reactflow-data'];
    if (!nodeType || !dataStr) return;
    try {
      onBlockAdd(nodeType, JSON.parse(dataStr));
    } catch { /* malformed payload — ignore the tap */ }
  };

  return (
    <div className="flex flex-col h-full" onClick={handleMobileTap}>
      <div className="p-3 border-b shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search signals & actions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
      <div className="p-2 space-y-1">

          {/* Provision Infrastructure (Slack @kestrel-workflow requests) — merged into Slack section below */}
          {/* Actions grouped by integration */}
          {(() => {
            // Unlisted groups fall to the end (999); vault/infisical/approval are
            // listed explicitly so Approval Gates sits below Infisical, right
            // above the Developer Requests section that renders after these groups.
            const integrationOrder = ['kubernetes', 'aws', 'gcp', 'kestrel', 'github', 'gitlab', 'slack', 'confluence', 'jira', 'linear', 'pagerduty', 'datadog', 'argocd', 'argo-rollouts', 'fluxcd', 'karpenter', 'kyverno', 'trivy', 'helm', 'jenkins', 'circleci', 'sonarcloud', 'okta', 'databricks', 'terraform', 'pulumi', 'cloudflare', 'posthog', 'vercel', 'railway', 'flyio', 'nebius', 'beam', 'daytona', 'supabase', 'planetscale', 'neon', 'clickhouse', 'aws-cost', 'gcp-cost', 'notion', 'vault', 'infisical', 'approval'];
            const sortedEntries = Object.entries(grouped).sort(([a], [b]) => {
              const ai = integrationOrder.indexOf(a);
              const bi = integrationOrder.indexOf(b);
              return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
            });
            return sortedEntries;
          })().map(([integration, acts]) => {
            const integrationInfo = integrations.find(i => i.id === integration);
            const connected = connectedSet.has(integration);
            const sectionKey = `actions-${integration}`;
            const filteredActs = acts.filter(
              a => !search || a.name.toLowerCase().includes(lowerSearch) || a.description.toLowerCase().includes(lowerSearch),
            );
            // Request signals are now in their own "Developer Requests" section
            const isSlackSection = integration === 'slack';
            const slackRequestSignals: typeof filteredRequestSignals = [];
            // For PagerDuty section, also include PD signal triggers
            const isPDSection = integration === 'pagerduty';
            const pdSignals = isPDSection ? filteredPDSignals : [];
            // For PostHog section, also include PostHog signal triggers
            const isPostHogSection = integration === 'posthog';
            const posthogSignals = isPostHogSection ? filteredPostHogSignals : [];
            // For Vercel section, also include Vercel signal triggers
            const isVercelSection = integration === 'vercel';
            const vercelSignals = isVercelSection ? filteredVercelSignals : [];
            // For Railway section, also include Railway signal triggers
            const isRailwaySection = integration === 'railway';
            const railwaySignals = isRailwaySection ? filteredRailwaySignals : [];
            // For Fly.io section, also include Fly.io signal triggers
            const isFlyioSection = integration === 'flyio';
            const flyioSignals = isFlyioSection ? filteredFlyioSignals : [];
            // For Nebius section, also include Nebius signal triggers
            const isNebiusSection = integration === 'nebius';
            const nebiusSignals = isNebiusSection ? filteredNebiusSignals : [];
            const isBeamSection = integration === 'beam';
            const beamSignals = isBeamSection ? filteredBeamSignals : [];
            const isDaytonaSection = integration === 'daytona';
            const daytonaSignals = isDaytonaSection ? filteredDaytonaSignals : [];
            const isSupabaseSection = integration === 'supabase';
            const supabaseSignals = isSupabaseSection ? filteredSupabaseSignals : [];
            const isPlanetScaleSection = integration === 'planetscale';
            const planetscaleSignals = isPlanetScaleSection ? filteredPlanetScaleSignals : [];
            const isNeonSection = integration === 'neon';
            const neonSignals = isNeonSection ? filteredNeonSignals : [];
            const isClickHouseSection = integration === 'clickhouse';
            const clickhouseSignals = isClickHouseSection ? filteredClickHouseSignals : [];
            const isJenkinsSection = integration === 'jenkins';
            const jenkinsSignals = isJenkinsSection ? filteredJenkinsSignals : [];
            const isCircleCISection = integration === 'circleci';
            const circleciSignals = isCircleCISection ? filteredCircleCISignals : [];
            const isSonarCloudSection = integration === 'sonarcloud';
            const sonarcloudSignals = isSonarCloudSection ? filteredSonarCloudSignals : [];
            const isOktaSection = integration === 'okta';
            const oktaSignals = isOktaSection ? filteredOktaSignals : [];
            const isDatabricksSection = integration === 'databricks';
            const databricksSignals = isDatabricksSection ? filteredDatabricksSignals : [];
            const isKyvernoSection = integration === 'kyverno';
            const kyvernoSignals = isKyvernoSection ? filteredKyvernoSignals : [];
            const isTrivySection = integration === 'trivy';
            const trivySignals = isTrivySection ? filteredTrivySignals : [];
            const isTerraformSection = integration === 'terraform';
            const terraformSignals = isTerraformSection ? filteredTerraformSignals : [];
            const isPulumiSection = integration === 'pulumi';
            const pulumiSignals = isPulumiSection ? filteredPulumiSignals : [];
            // For GitHub section, also include GitHub signal triggers
            const isGitHubSection = integration === 'github';
            const githubSignals = isGitHubSection ? filteredGitHubSignals : [];
            // GCP triggers live in the general "Google Cloud" section rather than a
            // standalone signals section, matching every other integration. There
            // is one trigger source ('gcp') covering all four GCP integrations, so
            // they attach to the 'gcp' group that security and data fold into.
            const isGCPSection = integration === 'gcp';
            const gcpSignals = isGCPSection ? filteredGCPSignals : [];
            // Kubernetes and AWS triggers render inside their own sections now that
            // both have action blocks, instead of the standalone signal sections
            // they used to have.
            const isK8sSection = integration === 'kubernetes';
            const k8sSignals = isK8sSection ? filteredK8sSignals : [];
            const isAWSSection = integration === 'aws';
            const awsSignals = isAWSSection ? filteredAWSSignals : [];

            if (filteredActs.length === 0 && slackRequestSignals.length === 0 && pdSignals.length === 0 && posthogSignals.length === 0 && vercelSignals.length === 0 && railwaySignals.length === 0 && flyioSignals.length === 0 && nebiusSignals.length === 0 && beamSignals.length === 0 && daytonaSignals.length === 0 && supabaseSignals.length === 0 && planetscaleSignals.length === 0 && neonSignals.length === 0 && clickhouseSignals.length === 0 && jenkinsSignals.length === 0 && circleciSignals.length === 0 && sonarcloudSignals.length === 0 && oktaSignals.length === 0 && databricksSignals.length === 0 && kyvernoSignals.length === 0 && trivySignals.length === 0 && terraformSignals.length === 0 && pulumiSignals.length === 0 && githubSignals.length === 0 && gcpSignals.length === 0 && k8sSignals.length === 0 && awsSignals.length === 0) return null;

            return (
              <div key={sectionKey}>
                <SectionHeader
                  label={integrationInfo?.name || (integration.charAt(0).toUpperCase() + integration.slice(1))}
                  icon={getIntegrationIcon(integration, isDark)}
                  expanded={!!expandedSections[sectionKey]}
                  onClick={() => toggleSection(sectionKey)}
                  badge={!connected ? 'Not connected' : undefined}
                />
                {expandedSections[sectionKey] && (
                  <div className="pl-2 space-y-1">
                    {/* PagerDuty signal triggers (PagerDuty section only) */}
                    {pdSignals.map(s => {
                      const pdConnected = connectedSet.has('pagerduty');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={pdConnected}
                                onDragStart={e => {
                                  if (!pdConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'pagerduty',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  pdConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {pdSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* PostHog signal triggers (PostHog section only) */}
                    {posthogSignals.map(s => {
                      const phConnected = connectedSet.has('posthog');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={phConnected}
                                onDragStart={e => {
                                  if (!phConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'posthog',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  phConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {posthogSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Vercel signal triggers (Vercel section only) */}
                    {vercelSignals.map(s => {
                      const vcConnected = connectedSet.has('vercel');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={vcConnected}
                                onDragStart={e => {
                                  if (!vcConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'vercel',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  vcConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {vercelSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Railway signal triggers (Railway section only) */}
                    {railwaySignals.map(s => {
                      const rwConnected = connectedSet.has('railway');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={rwConnected}
                                onDragStart={e => {
                                  if (!rwConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'railway',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  rwConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {railwaySignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Fly.io signal triggers (Fly.io section only) */}
                    {flyioSignals.map(s => {
                      const flyConnected = connectedSet.has('flyio');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={flyConnected}
                                onDragStart={e => {
                                  if (!flyConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'flyio',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  flyConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {flyioSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Nebius signal triggers (Nebius section only) */}
                    {nebiusSignals.map(s => {
                      const nbConnected = connectedSet.has('nebius');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={nbConnected}
                                onDragStart={e => {
                                  if (!nbConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'nebius',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  nbConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {nebiusSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Beam signal triggers (Beam section only) */}
                    {beamSignals.map(s => {
                      const beamConnected = connectedSet.has('beam');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={beamConnected}
                                onDragStart={e => {
                                  if (!beamConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'beam',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  beamConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {beamSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Daytona signal triggers (Daytona section only) */}
                    {daytonaSignals.map(s => {
                      const daytonaConnected = connectedSet.has('daytona');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={daytonaConnected}
                                onDragStart={e => {
                                  if (!daytonaConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'daytona',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  daytonaConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {daytonaSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Supabase signal triggers (Supabase section only) */}
                    {supabaseSignals.map(s => {
                      const supabaseConnected = connectedSet.has('supabase');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={supabaseConnected}
                                onDragStart={e => {
                                  if (!supabaseConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'supabase',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  supabaseConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {supabaseSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* PlanetScale signal triggers (PlanetScale section only) */}
                    {planetscaleSignals.map(s => {
                      const planetscaleConnected = connectedSet.has('planetscale');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={planetscaleConnected}
                                onDragStart={e => {
                                  if (!planetscaleConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'planetscale',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  planetscaleConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {planetscaleSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Neon signal triggers (Neon section only) */}
                    {neonSignals.map(s => {
                      const neonConnected = connectedSet.has('neon');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={neonConnected}
                                onDragStart={e => {
                                  if (!neonConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'neon',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  neonConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {neonSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* ClickHouse signal triggers (ClickHouse section only) */}
                    {clickhouseSignals.map(s => {
                      const clickhouseConnected = connectedSet.has('clickhouse');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={clickhouseConnected}
                                onDragStart={e => {
                                  if (!clickhouseConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'clickhouse',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  clickhouseConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {clickhouseSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Jenkins signal triggers (Jenkins section only) */}
                    {jenkinsSignals.map(s => {
                      const jenkinsConnected = connectedSet.has('jenkins');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={jenkinsConnected}
                                onDragStart={e => {
                                  if (!jenkinsConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'jenkins',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  jenkinsConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {jenkinsSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* CircleCI signal triggers (CircleCI section only) */}
                    {circleciSignals.map(s => {
                      const circleciConnected = connectedSet.has('circleci');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={circleciConnected}
                                onDragStart={e => {
                                  if (!circleciConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'circleci',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  circleciConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {circleciSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* SonarCloud signal triggers (SonarCloud section only) */}
                    {sonarcloudSignals.map(s => {
                      const sonarcloudConnected = connectedSet.has('sonarcloud');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={sonarcloudConnected}
                                onDragStart={e => {
                                  if (!sonarcloudConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'sonarcloud',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  sonarcloudConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {sonarcloudSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Okta signal triggers (Okta section only) */}
                    {oktaSignals.map(s => {
                      const oktaConnected = connectedSet.has('okta');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={oktaConnected}
                                onDragStart={e => {
                                  if (!oktaConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'okta',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  oktaConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {oktaSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Databricks signal triggers (Databricks section only) */}
                    {databricksSignals.map(s => {
                      const databricksConnected = connectedSet.has('databricks');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={databricksConnected}
                                onDragStart={e => {
                                  if (!databricksConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'databricks',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  databricksConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {databricksSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* GCP signal triggers (Google Cloud section only) */}
                    {gcpSignals.map(s => {
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={connected}
                                onDragStart={e => {
                                  if (!connected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'gcp',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  connected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {gcpSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Kubernetes signal triggers (Kubernetes section only) */}
                    {k8sSignals.map(s => (
                      <TooltipProvider key={s.id} delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              draggable={connected}
                              onDragStart={e => {
                                if (!connected) { e.preventDefault(); return; }
                                onDragStart(e, 'trigger', {
                                  signal_type: s.signal_type,
                                  signal_filter: s.default_filters,
                                  label: s.name,
                                  template_id: s.id,
                                  source: 'kubernetes',
                                });
                              }}
                              className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                connected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                              }`}
                            >
                              <div className="font-medium truncate">{s.name}</div>
                              <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs text-xs">
                            <p className="font-medium">{s.name}</p>
                            <p className="text-muted-foreground">{s.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ))}
                    {k8sSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* AWS signal triggers (AWS section only) */}
                    {awsSignals.map(s => (
                      <TooltipProvider key={s.id} delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              draggable={connected}
                              onDragStart={e => {
                                if (!connected) { e.preventDefault(); return; }
                                onDragStart(e, 'trigger', {
                                  signal_type: s.signal_type,
                                  signal_filter: s.default_filters,
                                  label: s.name,
                                  template_id: s.id,
                                  source: 'aws',
                                });
                              }}
                              className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                connected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                              }`}
                            >
                              <div className="font-medium truncate">{s.name}</div>
                              <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs text-xs">
                            <p className="font-medium">{s.name}</p>
                            <p className="text-muted-foreground">{s.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ))}
                    {awsSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Kyverno signal triggers (Kyverno section only) */}
                    {kyvernoSignals.map(s => {
                      const kyvernoConnected = connectedSet.has('kyverno');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={kyvernoConnected}
                                onDragStart={e => {
                                  if (!kyvernoConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'kyverno',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  kyvernoConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {kyvernoSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Trivy signal triggers (Trivy section only) */}
                    {trivySignals.map(s => {
                      const trivyConnected = connectedSet.has('trivy');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={trivyConnected}
                                onDragStart={e => {
                                  if (!trivyConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'trivy',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  trivyConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {trivySignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Terraform Cloud signal triggers (Terraform section only) */}
                    {terraformSignals.map(s => {
                      const terraformConnected = connectedSet.has('terraform');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={terraformConnected}
                                onDragStart={e => {
                                  if (!terraformConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'terraform',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  terraformConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {terraformSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* Pulumi Cloud signal triggers (Pulumi section only) */}
                    {pulumiSignals.map(s => {
                      const pulumiConnected = connectedSet.has('pulumi');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={pulumiConnected}
                                onDragStart={e => {
                                  if (!pulumiConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'pulumi',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  pulumiConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {pulumiSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* GitHub signal triggers (GitHub section only) */}
                    {githubSignals.map(s => {
                      const ghConnected = connectedSet.has('github');
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={ghConnected}
                                onDragStart={e => {
                                  if (!ghConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'github',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  ghConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate">{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {githubSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {/* /kestrel-workflow request signal triggers (Slack section only) */}
                    {slackRequestSignals.map(s => {
                      const isCloud = s.signal_type?.includes('cloud');
                      const isK8s = s.signal_type?.includes('k8s');
                      const tileIcon = isCloud
                        ? <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className="w-3.5 h-3.5 inline mr-1.5" />
                        : isK8s
                          ? <ShipWheel className="w-3.5 h-3.5 inline mr-1.5 text-blue-500" />
                          : <Rocket className="w-3.5 h-3.5 inline mr-1.5" />;
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable
                                onDragStart={e => onDragStart(e, 'trigger', {
                                  signal_type: s.signal_type,
                                  signal_filter: s.default_filters,
                                  label: s.name,
                                  template_id: s.id,
                                  source: 'request',
                                })}
                                className="p-2 rounded-md border bg-background hover:bg-accent cursor-grab active:cursor-grabbing transition-colors text-sm"
                              >
                                <div className="font-medium truncate flex items-center">{tileIcon}{s.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                    {slackRequestSignals.length > 0 && filteredActs.length > 0 && (
                      <div className="border-t my-1" />
                    )}
                    {filteredActs.map(a => (
                      <TooltipProvider key={a.id} delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              draggable={connected}
                              onDragStart={e => {
                                if (!connected) { e.preventDefault(); return; }
                                onDragStart(e, a.id === 'approval-refine-rca' ? 'refine_approval' : (a.id.startsWith('approval-') ? 'approval' : 'action'), {
                                  integration: a.integration,
                                  action: a.id,
                                  config: {},
                                  label: a.name,
                                  icon: a.icon,
                                  ...(a.id === 'approval-refine-rca' ? { approval_type: 'manual', max_rounds: 5 } : (a.id.startsWith('approval-') ? { approval_type: a.id === 'approval-wait-pr' ? 'pr_approval' : a.id.replace('approval-wait-', '') } : {})),
                                });
                              }}
                              className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                connected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                              }`}
                            >
                              <div className="font-medium truncate flex items-center gap-1.5">
                                {a.integration === 'aws-cost' && (
                                  <img src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" className="w-3.5 h-3.5 shrink-0" />
                                )}
                                {a.name}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{a.description}</div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs text-xs">
                            <p className="font-medium">{a.name}</p>
                            <p className="text-muted-foreground">{a.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Developer Requests — per-integration triggers */}
          {filteredRequestSignals.length > 0 && (() => {
            const isDark = document.documentElement.classList.contains('dark');
            const getReqIcon = (icon: string) => {
              switch (icon) {
                case 'kubernetes': return <ShipWheel className="w-3.5 h-3.5 text-blue-500" />;
                case 'cloud': return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="" className="w-3.5 h-3.5" />;
                case 'gcp': return <GCPIcon className="w-3.5 h-3.5" />;
                case 'cloudflare': return <img src={cloudflareIcon} alt="" className="w-3.5 h-3.5" />;
                case 'pagerduty': return <img src={pagerdutyColorIcon} alt="" className="w-3.5 h-3.5" />;
                case 'datadog': return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="" className="w-3.5 h-3.5" />;
                case 'argocd': return <img src={argocdIcon} alt="" className="w-3.5 h-3.5" />;
                case 'argo-rollouts': return <img src={argocdIcon} alt="" className="w-3.5 h-3.5" />;
                case 'fluxcd': return <img src={fluxcdIcon} alt="" className="w-3.5 h-3.5" />;
                case 'karpenter': return <img src={karpenterIcon} alt="" className="w-3.5 h-3.5" />;
                case 'kyverno': return <img src={kyvernoIcon} alt="" className="w-3.5 h-3.5" />;
                case 'trivy': return <img src={trivyIcon} alt="" className="w-3.5 h-3.5" />;
                case 'github': return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="" className="w-3.5 h-3.5" />;
                case 'gitlab': return <img src={gitlabIcon} alt="" className="w-3.5 h-3.5" />;
                case 'helm': return <img src={isDark ? helmDarkIcon : helmLightIcon} alt="" className="w-3.5 h-3.5" />;
                case 'vercel': return <img src={isDark ? vercelLightIcon : vercelIcon} alt="" className="w-3.5 h-3.5" />;
                case 'railway': return <img src={railwayIcon} alt="" className="w-3.5 h-3.5 object-contain" style={{ filter: isDark ? 'invert(1)' : 'none' }} />;
                case 'flyio': return <img src={flyioIcon} alt="" className="w-3.5 h-3.5 object-contain" />;
                case 'nebius': return <img src={nebiusIcon} alt="" className="w-3.5 h-3.5 object-contain" />;
                case 'beam': return <BeamIcon className="w-3.5 h-3.5 text-foreground" />;
                case 'daytona': return <DaytonaIcon className="w-3.5 h-3.5 text-foreground" />;
                case 'supabase': return <img src={supabaseIcon} alt="Supabase" className="w-3.5 h-3.5 object-contain" />;
                case 'planetscale': return <PlanetScaleIcon className="w-3.5 h-3.5 text-foreground" />;
                case 'neon': return <img src={neonIcon} alt="Neon" className="w-3.5 h-3.5 object-contain" />;
                case 'clickhouse': return <img src={clickhouseIcon} alt="ClickHouse" className="w-3.5 h-3.5 object-contain" />;
                case 'jenkins': return <img src={jenkinsIcon} alt="Jenkins" className="w-3.5 h-3.5 object-contain" />;
                case 'circleci': return <CircleCIIcon className="w-3.5 h-3.5 text-foreground" />;
                case 'sonarcloud': return <SonarCloudIcon className="w-3.5 h-3.5 text-foreground" />;
                case 'okta': return <OktaIcon className="w-3.5 h-3.5 text-foreground" />;
                case 'databricks': return <img src={databricksIcon} alt="Databricks" className="w-3.5 h-3.5 object-contain" />;
                case 'terraform': return <img src={terraformIcon} alt="Terraform" className="w-3.5 h-3.5 object-contain" />;
                case 'pulumi': return <img src={pulumiIcon} alt="Pulumi" className="w-3.5 h-3.5 object-contain" />;
                case 'vault': return <img src={vaultIcon} alt="Vault" className="w-3.5 h-3.5 object-contain" />;
                case 'infisical': return <img src={infisicalIcon} alt="Infisical" className="w-3.5 h-3.5 object-contain rounded-sm" />;
                default: return <Send className="w-3.5 h-3.5 text-primary" />;
              }
            };
            // Request-signal templates share a generic icon (e.g. "database" for
            // all DB integrations), so prefer the request category for a
            // brand-specific icon and fall back to the template's icon field.
            const getReqIconForSignal = (s: typeof filteredRequestSignals[number]) => {
              const cat = (s.default_filters?.request_categories || [])[0];
              const known = ['kubernetes', 'cloud', 'gcp', 'cloudflare', 'pagerduty', 'datadog', 'argocd', 'argo-rollouts', 'fluxcd', 'karpenter', 'kyverno', 'trivy', 'github', 'gitlab', 'helm', 'vercel', 'railway', 'flyio', 'nebius', 'beam', 'daytona', 'supabase', 'planetscale', 'neon', 'clickhouse', 'jenkins', 'circleci', 'sonarcloud', 'okta', 'databricks', 'terraform', 'pulumi', 'vault', 'infisical'];
              if (cat && known.includes(cat)) return getReqIcon(cat);
              return getReqIcon(s.icon);
            };
            // Map a request signal's category to the integration status id(s)
            // that must be connected for the trigger to be usable.
            const getReqIntegrationIds = (s: typeof filteredRequestSignals[number]): string[] => {
              const cat = (s.default_filters?.request_categories || [])[0];
              switch (cat) {
                case 'k8s': return ['kubernetes'];
                // Any one connected cloud makes a cloud request usable, so GCP
                // belongs here too — without it a GCP-only customer sees cloud
                // requests as unavailable.
                case 'cloud': return ['aws', 'gcp', 'oci'];
                case 'general': return []; // general requests need no integration
                default: return cat ? [cat] : [];
              }
            };
            const isReqSignalConnected = (s: typeof filteredRequestSignals[number]) => {
              const ids = getReqIntegrationIds(s);
              if (ids.length === 0) return true;
              return ids.some(id => connectedSet.has(id));
            };
            return (
              <div>
                <SectionHeader
                  label="Developer Requests"
                  icon={<Send className="w-4 h-4 text-primary" />}
                  expanded={!!expandedSections['dev-requests']}
                  onClick={() => toggleSection('dev-requests')}
                />
                {expandedSections['dev-requests'] && (
                  <div className="pl-2 space-y-1">
                    {filteredRequestSignals.map(s => {
                      const reqConnected = isReqSignalConnected(s);
                      return (
                        <TooltipProvider key={s.id} delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable={reqConnected}
                                onDragStart={e => {
                                  if (!reqConnected) { e.preventDefault(); return; }
                                  onDragStart(e, 'trigger', {
                                    signal_type: s.signal_type,
                                    signal_filter: s.default_filters,
                                    label: s.name,
                                    template_id: s.id,
                                    source: 'request',
                                  });
                                }}
                                className={`p-2 rounded-md border bg-background transition-colors text-sm ${
                                  reqConnected ? 'hover:bg-accent cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'
                                }`}
                              >
                                <div className="font-medium truncate flex items-center gap-1.5">
                                  {getReqIconForSignal(s)}
                                  {s.name}
                                  {!reqConnected && (
                                    <span className="ml-auto text-[10px] font-normal text-muted-foreground border rounded px-1 py-0.5 shrink-0">Not connected</span>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{s.name}</p>
                              <p className="text-muted-foreground">{s.description}</p>
                              {!reqConnected && (
                                <p className="text-muted-foreground mt-1">Connect this integration to use this trigger.</p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Condition block — inside action groups, after Approval Gates */}
          {(!search || 'condition'.includes(lowerSearch) || 'if'.includes(lowerSearch) || 'branch'.includes(lowerSearch)) && (
            <div>
              <SectionHeader
                label="Conditions"
                icon={<GitFork className="w-4 h-4 text-zinc-900 dark:text-zinc-100" />}
                expanded={expandedSections['conditions'] ?? false}
                onClick={() => toggleSection('conditions')}
              />
              {(expandedSections['conditions'] ?? false) && (
                <div className="pl-2 pr-1 pb-2 space-y-1">
                  <div
                    draggable
                    onDragStart={e => {
                      onDragStart(e, 'condition', {
                        label: 'Condition',
                        field: '',
                        operator: 'equals',
                        value: '',
                      });
                    }}
                    className="p-2 rounded-md border bg-background hover:bg-accent cursor-grab active:cursor-grabbing transition-colors text-sm"
                  >
                    <div className="font-medium truncate">If / Else</div>
                    <div className="text-xs text-muted-foreground truncate">Branch based on a field value</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Flow Control blocks — Poll Until (loop) and For Each (fan-out) */}
          {(!search || 'poll until'.includes(lowerSearch) || 'loop'.includes(lowerSearch) || 'wait'.includes(lowerSearch) || 'poll'.includes(lowerSearch) || 'for each'.includes(lowerSearch) || 'fan out'.includes(lowerSearch) || 'iterate'.includes(lowerSearch)) && (
            <div>
              <SectionHeader
                label="Flow Control"
                icon={<RefreshCw className="w-4 h-4 text-zinc-900 dark:text-zinc-100" />}
                expanded={expandedSections['flow-control'] ?? false}
                onClick={() => toggleSection('flow-control')}
              />
              {(expandedSections['flow-control'] ?? false) && (
                <div className="pl-2 pr-1 pb-2 space-y-1">
                  {(!search || 'poll until'.includes(lowerSearch) || 'loop'.includes(lowerSearch) || 'wait'.includes(lowerSearch) || 'poll'.includes(lowerSearch)) && (
                  <div
                    draggable
                    onDragStart={e => {
                      onDragStart(e, 'loop', {
                        label: 'Poll Until',
                        integration: '',
                        action: '',
                        config: {},
                        condition: { field: '', operator: 'equals', value: '' },
                        interval_seconds: 60,
                        timeout_minutes: 60,
                      });
                    }}
                    className="p-2 rounded-md border bg-background hover:bg-accent cursor-grab active:cursor-grabbing transition-colors text-sm"
                  >
                    <div className="font-medium truncate">Poll Until</div>
                    <div className="text-xs text-muted-foreground truncate">Repeat an action until a condition is met</div>
                  </div>
                  )}
                  {(!search || 'for each'.includes(lowerSearch) || 'fan out'.includes(lowerSearch) || 'iterate'.includes(lowerSearch) || 'loop'.includes(lowerSearch)) && (
                  <div
                    draggable
                    onDragStart={e => {
                      onDragStart(e, 'for_each', {
                        label: 'For Each',
                        integration: '',
                        action: '',
                        config: {},
                        items_path: '',
                        max_items: 25,
                        continue_on_error: true,
                      });
                    }}
                    className="p-2 rounded-md border bg-background hover:bg-accent cursor-grab active:cursor-grabbing transition-colors text-sm"
                  >
                    <div className="font-medium truncate">For Each</div>
                    <div className="text-xs text-muted-foreground truncate">Run an action once per item of a list</div>
                  </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Schedule triggers — no integration required, always draggable */}
          {filteredScheduleSignals.length > 0 && (
          <div>
          <SectionHeader
            label="Schedule Triggers"
            icon={<Clock className="w-4 h-4 text-zinc-900 dark:text-zinc-100" />}
            expanded={!!expandedSections['schedule-signals']}
            onClick={() => toggleSection('schedule-signals')}
          />
          {expandedSections['schedule-signals'] && (
            <div className="pl-2 space-y-1">
              {filteredScheduleSignals.map(s => (
                <TooltipProvider key={s.id} delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        draggable
                        onDragStart={e => {
                          onDragStart(e, 'trigger', {
                            signal_type: s.signal_type,
                            signal_filter: s.default_filters,
                            label: s.name,
                            template_id: s.id,
                            source: 'schedule',
                          });
                        }}
                        className="p-2 rounded-md border bg-background transition-colors text-sm hover:bg-accent cursor-grab active:cursor-grabbing"
                      >
                        <div className="font-medium truncate">{s.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{s.description}</div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs text-xs">
                      <p className="font-medium">{s.name}</p>
                      <p className="text-muted-foreground">{s.description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          )}
          </div>
          )}
        </div>

      {/* Custom Blocks — grouped by integration_name */}
      {/* Custom Integrations Section */}
      {customBlocks && customBlocks.length > 0 && (() => {
        const grouped = new Map<string, typeof customBlocks>();
        for (const b of customBlocks) {
          const key = b.integration_name || 'Custom';
          if (!grouped.has(key)) grouped.set(key, []);
          grouped.get(key)!.push(b);
        }
        return (
          <div className="px-2 pb-3 space-y-1">
            <div className="px-2 pt-3 pb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Custom Integrations</span>
            </div>
            {Array.from(grouped.entries()).map(([integName, blocks]) => {
              const sectionKey = `custom-${integName}`;
              const groupIconLight = blocks.find(b => b.icon_url)?.icon_url || '';
              const groupIconDark = blocks.find(b => b.icon_url_dark)?.icon_url_dark || '';
              const groupIcon = isDark ? (groupIconDark || groupIconLight) : (groupIconLight || groupIconDark);
              const groupColor = blocks.find(b => b.color)?.color || '#7c6f9f';
              const filteredBlocks = blocks.filter(
                b => !search || b.name.toLowerCase().includes(lowerSearch) || b.description.toLowerCase().includes(lowerSearch) || integName.toLowerCase().includes(lowerSearch),
              );
              if (filteredBlocks.length === 0 && search) return null;
              return (
                <div key={sectionKey}>
                  <SectionHeader
                    label={integName}
                    icon={groupIcon ? (
                      <img src={groupIcon} alt={integName} className="w-4 h-4 rounded object-contain" />
                    ) : (
                      <span className="w-4 h-4 rounded" style={{ backgroundColor: groupColor }} />
                    )}
                    expanded={!!expandedSections[sectionKey]}
                    onClick={() => toggleSection(sectionKey)}
                  />
                  {expandedSections[sectionKey] && (
                    <div className="pl-2 space-y-1">
                      {filteredBlocks.map(b => {
                        const blockIcon = isDark
                          ? (b.icon_url_dark || b.icon_url || groupIconDark || groupIconLight)
                          : (b.icon_url || b.icon_url_dark || groupIconLight || groupIconDark);
                        const isTrigger = b.block_type === 'trigger';
                        return (
                        <div key={b.id} className="relative group/block">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                draggable
                                onDragStart={e => {
                                  if (isTrigger) {
                                    // Extract event type from description if present (e.g., "Triggers on email.delivered events")
                                    const descMatch = b.description?.match(/Triggers on (\S+) events/);
                                    const eventType = descMatch?.[1] || 'any';
                                    e.dataTransfer.setData('application/reactflow-type', 'trigger');
                                    e.dataTransfer.setData('application/reactflow-data', JSON.stringify({
                                      signal_type: eventType,
                                      signal_filter: {},
                                      label: b.name,
                                      template_id: `custom-trigger-${b.id}`,
                                      source: 'custom_webhook',
                                      custom_block_id: b.id,
                                      custom_icon_url: groupIconLight || b.icon_url || groupIconDark || b.icon_url_dark || '',
                                      custom_icon_url_dark: groupIconDark || b.icon_url_dark || groupIconLight || b.icon_url || '',
                                      custom_color: b.color || groupColor,
                                      integration_name: integName,
                                    }));
                                  } else {
                                    e.dataTransfer.setData('application/reactflow-type', 'action');
                                    e.dataTransfer.setData('application/reactflow-data', JSON.stringify({
                                      integration: 'custom',
                                      action: `custom-${b.id}`,
                                      config: {},
                                      label: b.name,
                                      description: b.description || '',
                                      icon: 'puzzle',
                                      custom_block_id: b.id,
                                      custom_icon_url: groupIconLight || b.icon_url || groupIconDark || b.icon_url_dark || '',
                                      custom_icon_url_dark: groupIconDark || b.icon_url_dark || groupIconLight || b.icon_url || '',
                                      custom_color: b.color || groupColor,
                                      integration_name: integName,
                                      custom_parameters: b.parameters || [],
                                    }));
                                  }
                                  e.dataTransfer.effectAllowed = 'move';
                                }}
                                className="p-2 pr-14 rounded-md border bg-background hover:bg-accent cursor-grab active:cursor-grabbing transition-colors text-sm"
                              >
                                <div className="font-medium truncate flex items-center gap-1.5">
                                  {b.name}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">{b.description}</div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs text-xs">
                              <p className="font-medium">{b.name}</p>
                              <p className="text-muted-foreground">{b.description}</p>
                              <p className="text-muted-foreground mt-1">{isTrigger ? 'Webhook trigger' : `${b.method} request`}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {onEditCustomBlock && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onEditCustomBlock(b); }}
                            className="absolute top-1.5 right-7 p-1 rounded opacity-0 group-hover/block:opacity-100 hover:bg-accent text-muted-foreground hover:text-foreground transition-all"
                            title="Edit block"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                        {onDeleteCustomBlock && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ id: b.id, name: b.name }); }}
                            className="absolute top-1.5 right-1.5 p-1 rounded opacity-0 group-hover/block:opacity-100 hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                            title="Delete block"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}
      </div>

      {/* Create Custom Block + Request Integration — pinned bottom */}
      <div className="shrink-0 border-t">
      <div className="p-2 pt-3 space-y-1">
        {onCreateCustomBlock && (
          <button
            onClick={onCreateCustomBlock}
            className="flex items-center gap-2 w-full p-2 rounded-md hover:bg-accent transition-colors text-sm text-muted-foreground hover:text-foreground"
          >
            <Puzzle className="w-3.5 h-3.5" />
            <span>Create Custom Workflow Block</span>
          </button>
        )}
      </div>

      {/* Request Integration — hidden in the public demo builder (requires a
          tenant to file the request against) */}
      {!IS_DEMO_MODE && <RequestIntegrationForm />}
      </div>

      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete custom block</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-medium">{deleteConfirm?.name}</span>? This will permanently remove the block definition and its stored credentials. Existing workflow executions that used this block are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBlock} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RequestIntegrationForm() {
  const [open, setOpen] = useState(false);
  const [toolName, setToolName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!toolName.trim()) return;
    setSubmitting(true);
    try {
      await apiClient.requestIntegration(toolName.trim(), description.trim());
      setSubmitted(true);
      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setToolName('');
        setDescription('');
      }, 2000);
    } catch {
      // silent fail
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <div className="p-2 border-t">
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 w-full p-2 rounded-md hover:bg-accent transition-colors text-sm text-muted-foreground hover:text-foreground"
        >
          <PlusCircle className="w-3.5 h-3.5" />
          <span>Request an integration</span>
        </button>
      </div>
    );
  }

  return (
    <div className="p-3 border-t space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium">Request Integration</span>
        <button onClick={() => setOpen(false)} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
      </div>
      <Input
        placeholder="Tool name (e.g. Opsgenie)"
        value={toolName}
        onChange={e => setToolName(e.target.value)}
        className="h-8 text-xs"
        disabled={submitting || submitted}
      />
      <Textarea
        placeholder="Brief description of what you need..."
        value={description}
        onChange={e => setDescription(e.target.value)}
        rows={2}
        className="text-xs resize-none"
        disabled={submitting || submitted}
      />
      <Button
        size="sm"
        className="w-full h-7 text-xs gap-1.5"
        onClick={handleSubmit}
        disabled={!toolName.trim() || submitting || submitted}
      >
        {submitted ? (
          <><CheckCircle2 className="w-3 h-3" /> Submitted</>
        ) : submitting ? (
          <><Loader2 className="w-3 h-3 animate-spin" /> Submitting...</>
        ) : (
          <><Send className="w-3 h-3" /> Submit Request</>
        )}
      </Button>
    </div>
  );
}

function SectionHeader({
  label,
  icon,
  expanded,
  onClick,
  badge,
}: {
  label: string;
  icon: React.ReactNode;
  expanded: boolean;
  onClick: () => void;
  badge?: string;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 w-full p-2 rounded-md hover:bg-accent transition-colors text-sm font-medium"
    >
      {expanded ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
      {icon}
      <span className="flex-1 text-left truncate">{label}</span>
      {badge && (
        <Badge variant="outline" className="text-xs shrink-0">
          {badge}
        </Badge>
      )}
    </button>
  );
}
